<template>
  <div v-if="!hasPermission('View Plan Dashboard')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar />
    <div class="container-xxl">
    <div class="row g-5 g-xl-8">
      <!-- Cards de Resumo -->
      <div class="col-xl-3">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Total de Planos</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder text-primary">{{ dashboardData.summary?.total_plans || 0 }}</span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Ativos: {{ dashboardData.summary?.active_plans || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Total de Assinantes</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder text-success">{{ dashboardData.summary?.total_subscribers || 0 }}</span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Ativos: {{ dashboardData.summary?.active_subscribers || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Receita Total</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder text-info">R$ {{ formatCurrency(dashboardData.summary?.total_revenue || 0) }}</span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Este mês: R$ {{ formatCurrency(dashboardData.summary?.current_month_revenue || 0) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Cobranças Pendentes</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder text-warning">{{ dashboardData.summary?.pending_charges || 0 }}</span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Total: R$ {{ formatCurrency(dashboardData.summary?.pending_charges_amount || 0) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-5 g-xl-8 mt-2">
      <!-- Total de Cancelamentos -->
      <div class="col-xl-4">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Total de Cancelamentos</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder text-danger">{{ dashboardData.summary?.total_cancellations || 0 }}</span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Este mês: {{ dashboardData.summary?.current_month_cancellations || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Taxa de Cancelamento -->
      <div class="col-xl-4">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Taxa de Cancelamento</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder" :class="getCancellationRateClass(dashboardData.summary?.cancellation_rate)">
                {{ dashboardData.summary?.cancellation_rate || 0 }}%
              </span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Sobre o total de assinantes</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Assinantes com Vencimento Próximo -->
      <div class="col-xl-4">
        <div class="card card-flush h-xl-100">
          <div class="card-header pt-5">
            <div class="card-title d-flex flex-column">
              <span class="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">Vencimento Próximo</span>
            </div>
          </div>
          <div class="card-body pt-2 pb-4 d-flex flex-wrap align-items-center">
            <div class="d-flex flex-center me-7 pt-2">
              <span class="fs-3x fw-bolder text-warning">{{ dashboardData.summary?.subscribers_expiring_soon || 0 }}</span>
            </div>
            <div class="d-flex flex-column content-justify-center w-100">
              <span class="fs-6 text-gray-500 pt-1">Nos próximos 30 dias</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-5 g-xl-8 mt-2">
      <!-- Gráfico de Receita -->
      <div class="col-xl-8">
        <div class="card">
          <div class="card-header">
            <div class="card-title">
              <h3 class="fw-bold m-0">Receita dos Últimos 6 Meses</h3>
            </div>
          </div>
          <div class="card-body">
            <apexchart
              v-if="revenueChartOptions"
              type="area"
              height="350"
              :options="revenueChartOptions"
              :series="revenueChartSeries"
            />
          </div>
        </div>
      </div>

      <!-- Assinantes por Status -->
      <div class="col-xl-4">
        <div class="card">
          <div class="card-header">
            <div class="card-title">
              <h3 class="fw-bold m-0">Assinantes por Status</h3>
            </div>
          </div>
          <div class="card-body">
            <apexchart
              v-if="statusChartOptions"
              type="donut"
              height="350"
              :options="statusChartOptions"
              :series="statusChartSeries"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row g-5 g-xl-8 mt-2">
      <!-- Assinaturas dos Últimos 6 Meses -->
      <div class="col-xl-6">
        <div class="card">
          <div class="card-header">
            <div class="card-title">
              <h3 class="fw-bold m-0">Assinaturas dos Últimos 6 Meses</h3>
            </div>
          </div>
          <div class="card-body">
            <apexchart
              v-if="subscriptionsChartOptions"
              type="bar"
              height="350"
              :options="subscriptionsChartOptions"
              :series="subscriptionsChartSeries"
            />
          </div>
        </div>
      </div>

      <!-- Cancelamentos dos Últimos 6 Meses -->
      <div class="col-xl-6">
        <div class="card">
          <div class="card-header">
            <div class="card-title">
              <h3 class="fw-bold m-0">Cancelamentos dos Últimos 6 Meses</h3>
            </div>
          </div>
          <div class="card-body">
            <apexchart
              v-if="cancellationsChartOptions"
              type="bar"
              height="350"
              :options="cancellationsChartOptions"
              :series="cancellationsChartSeries"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row g-5 g-xl-8 mt-2">
      <!-- Assinantes por Plano -->
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header">
            <div class="card-title">
              <h3 class="fw-bold m-0">Assinantes por Plano</h3>
            </div>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Plano</th>
                    <th class="text-end">Assinantes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in dashboardData.subscribers_by_plan" :key="item.plan_id">
                    <td>{{ item.plan_name }}</td>
                    <td class="text-end">{{ item.subscribers_count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-5 g-xl-8 mt-2 mb-5">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header">
            <div class="card-title">
              <h3 class="fw-bold m-0">Alertas</h3>
            </div>
          </div>
          <div class="card-body">
            <div v-if="dashboardData.summary?.subscribers_expiring_soon > 0" class="alert alert-warning">
              <strong>Atenção!</strong> Existem {{ dashboardData.summary.subscribers_expiring_soon }} assinante(s) com vencimento nos próximos 30 dias.
            </div>
            <div v-if="dashboardData.summary?.pending_charges > 0" class="alert alert-danger">
              <strong>Atenção!</strong> Existem {{ dashboardData.summary.pending_charges }} cobrança(s) pendente(s) no valor total de R$ {{ formatCurrency(dashboardData.summary.pending_charges_amount) }}.
            </div>
            <div v-if="dashboardData.summary?.subscribers_expiring_soon === 0 && dashboardData.summary?.pending_charges === 0" class="alert alert-success">
              <strong>Tudo certo!</strong> Não há alertas no momento.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import VueApexCharts from 'vue3-apexcharts/src/vue3-apexcharts';
import Permission from "../../helpers/permission";

export default {
  name: "PlanDashboard",
  components: {
    Toolbar,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      dashboardData: {
        summary: {},
        subscribers_by_status: {},
        revenue_last_6_months: [],
        subscribers_by_plan: [],
        subscriptions_last_6_months: [],
      },
      revenueChartOptions: null,
      revenueChartSeries: [],
      statusChartOptions: null,
      statusChartSeries: [],
      subscriptionsChartOptions: null,
      subscriptionsChartSeries: [],
      cancellationsChartOptions: null,
      cancellationsChartSeries: [],
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  mounted() {
    this.loadDashboard();
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    loadDashboard() {
      http.get(`/places/${this.place.id_place}/plans/dashboard`).then((response) => {
        this.dashboardData = response.data;
        this.updateCharts();
      }).catch((error) => {
        console.error('Erro ao carregar dashboard:', error);
      });
    },
    updateCharts() {
      // Gráfico de Receita
      const revenueData = this.dashboardData.revenue_last_6_months || [];
      this.revenueChartOptions = {
        chart: {
          type: 'area',
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth' },
        xaxis: {
          categories: revenueData.map(item => {
            const [year, month] = item.month.split('-');
            return new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
          }),
        },
        yaxis: {
          labels: {
            formatter: (value) => 'R$ ' + this.formatCurrency(value)
          }
        },
        tooltip: {
          y: {
            formatter: (value) => 'R$ ' + this.formatCurrency(value)
          }
        },
        colors: ['#00ADA3'],
      };
      this.revenueChartSeries = [{
        name: 'Receita',
        data: revenueData.map(item => item.total)
      }];

      // Gráfico de Status
      const statusData = this.dashboardData.subscribers_by_status || {};
      const statusLabels = Object.keys(statusData);
      const statusValues = Object.values(statusData);
      
      this.statusChartOptions = {
        chart: { type: 'donut' },
        labels: statusLabels.map(label => {
          const labels = {
            'pending': 'Pendente',
            'active': 'Ativo',
            'cancelled': 'Cancelado',
            'payment_failed': 'Falha no Pagamento',
            'finished': 'Finalizado'
          };
          return labels[label] || label;
        }),
        legend: { position: 'bottom' },
      };
      this.statusChartSeries = statusValues;

      // Gráfico de Assinaturas
      const subscriptionsData = this.dashboardData.subscriptions_last_6_months || [];
      this.subscriptionsChartOptions = {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        dataLabels: { enabled: true },
        xaxis: {
          categories: subscriptionsData.map(item => {
            const [year, month] = item.month.split('-');
            return new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
          }),
        },
        colors: ['#FFAB2D'],
      };
      this.subscriptionsChartSeries = [{
        name: 'Assinaturas',
        data: subscriptionsData.map(item => item.count)
      }];

      // Gráfico de Cancelamentos
      const cancellationsData = this.dashboardData.cancellations_last_6_months || [];
      this.cancellationsChartOptions = {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        dataLabels: { enabled: true },
        xaxis: {
          categories: cancellationsData.map(item => {
            const [year, month] = item.month.split('-');
            return new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
          }),
        },
        colors: ['#F1416C'],
      };
      this.cancellationsChartSeries = [{
        name: 'Cancelamentos',
        data: cancellationsData.map(item => item.count)
      }];
    },
    getCancellationRateClass(rate) {
      if (!rate) return 'text-gray-500';
      if (rate <= 5) return 'text-success';
      if (rate <= 10) return 'text-warning';
      return 'text-danger';
    },
    hasPermission(permission) {
      return Permission.has(permission);
    },
  }
}
</script>

<style scoped>
.card-flush {
  border-radius: 0.475rem;
}
</style>

